import type { GeoPoint } from '@metro/location'
import {
  haversineMeters,
  type GeoStation,
  type NearestStationProvider,
} from './nearestStationProvider'
import type { SchemaStation } from '../schema/types'

/**
 * Adapts the offline metro station database (`schema.json`'s
 * `SchemaStation[]`) to the metro location-consumer service's
 * {@link NearestStationProvider} seam (spec §2b). This domain coupling
 * lives host-side, never inside the product-agnostic `Location` module.
 */
export class SchemaNearestStationProvider implements NearestStationProvider {
  private readonly geoStations: readonly GeoStation[]

  constructor(stations: readonly SchemaStation[]) {
    // Every station in production `schema.json` carries `coordinate`; the
    // guard drops any record that lacks one (stripped fixtures / schema
    // drift) so the provider never resolves to an undefined point.
    this.geoStations = stations
      .filter(
        (s): s is SchemaStation & { coordinate: NonNullable<SchemaStation['coordinate']> } =>
          s.coordinate !== undefined,
      )
      .map((s) => ({
        id: s.id,
        name: s.localName,
        point: { latitude: s.coordinate.latitude, longitude: s.coordinate.longitude },
      }))
  }

  async nearestStations(to: GeoPoint, limit: number): Promise<GeoStation[]> {
    return [...this.geoStations]
      .sort((x, y) => haversineMeters(x.point, to) - haversineMeters(y.point, to))
      .slice(0, limit)
  }
}
